import React, { useEffect, useState, useCallback } from 'react';
import MixpanelService from '../../../services/MixpanelService';
import Icon from '../../../_shared/components/Icon';
import Loading from '../../../_shared/components/Loading';
import mixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import {
  CAROUSEL_NAVIGATE,
  CAROUSEL_LENGTH,
  RetailMediaType,
  RetailPlatforms
} from '../../../_shared/constants/retail';
import { isMobileDevice } from '../../../_shared/utils/userAgent';

import {
  CarouselContainer,
  ThumbnailsContainer,
  CarouselThumbnails,
  CarouselItem,
  CarouselMain,
  CarouselMainPhoto,
  StyledCarouselPrev,
  StyledCarouselNext,
  CarouselCounter,
  PhotoOverlay,
  CarouselPhoto,
  CarouselVideo,
  CarouselMainVideo,
  LabelContainer,
  StyledCarouselLabel
} from './Styles';

const Carousel = ({
  media,
  isGalleryOpen,
  onGalleryOpen,
  isLightbox,
  selectedPhotoIdx,
  retailTrade,
  tradeDetails,
  dealerName
}) => {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [wasClicked, setWasClicked] = useState(false);
  const [multiple, setMultiple] = useState(0);
  const [numberInCarousel, setNumberInCarousel] = useState(
    CAROUSEL_LENGTH.DEFAULT
  );
  const mp = MixpanelService.get();

  useEffect(() => {
    setNumberInCarousel(
      isLightbox ? CAROUSEL_LENGTH.LIGHTBOX : CAROUSEL_LENGTH.DEFAULT
    );
  }, [isLightbox]);

  useEffect(() => {
    if (isLightbox) setSelectedPhotoIndex(selectedPhotoIdx);
  }, [isLightbox, selectedPhotoIdx]);

  // This calculates the offset required to shift the carousel thumbnail
  const calculateLeftShift = useCallback(() => {
    const max = media?.length - 1;
    let offset = 0;
    /* Starting with the 4th image, start shifting to the left */
    if (
      selectedPhotoIndex >= numberInCarousel - 1 &&
      selectedPhotoIndex !== max
    ) {
      offset = selectedPhotoIndex - (numberInCarousel - 1);
      /* If it's the last image, shift it so it shows the last image on the right side */
    } else if (selectedPhotoIndex === max) {
      offset = max - (numberInCarousel - 1);
    }

    // Re-render only if the new photo is outside the current carousel view
    if (
      selectedPhotoIndex < multiple ||
      selectedPhotoIndex - (numberInCarousel - 1) > multiple
    )
      setMultiple(offset);
  }, [media?.length, selectedPhotoIndex, multiple, numberInCarousel]);

  // Handles the previous button on the carousel
  const onCarouselPrev = useCallback(() => {
    setSelectedPhotoIndex(index =>
      index !== 0 ? index - 1 : media?.length - 1
    );
    setWasClicked(false);
  }, [media?.length]);

  // Handles the next button on the carousel
  const onCarouselNext = useCallback(() => {
    setSelectedPhotoIndex(index =>
      index !== media?.length - 1 ? index + 1 : 0
    );
    setWasClicked(false);
  }, [media?.length]);

  // Handles clicking the carousel thumbnails
  const onCarouselClick = index => {
    setSelectedPhotoIndex(index);
    setWasClicked(true);

    mp.track(mixpanelConfig.names.clickRetailVdpThumbnail, {
      source: `Retail VDP${isLightbox ? ' expanded view' : ''}`,
      retail_guid: retailTrade?.guid,
      year: tradeDetails.year,
      make: tradeDetails.make,
      model: tradeDetails.model,
      vin: tradeDetails.vin,
      trim: tradeDetails.trim,
      platform: isMobileDevice() ? RetailPlatforms.Mobile : RetailPlatforms.Web,
      is_retail_customer:
        JSON.parse(window.localStorage.getItem('auth'))?.user === undefined,
      dealer: dealerName
    });
  };

  useEffect(() => {
    if (!wasClicked) calculateLeftShift();
  }, [calculateLeftShift, wasClicked]);

  // Handles arrow keys for carousel
  const onCarouselNavigate = useCallback(
    e => {
      if (e.keyCode === CAROUSEL_NAVIGATE.prev) {
        onCarouselPrev();
      } else if (e.keyCode === CAROUSEL_NAVIGATE.next) {
        onCarouselNext();
      }
    },
    [onCarouselNext, onCarouselPrev]
  );

  useEffect(() => {
    window.addEventListener('keydown', onCarouselNavigate);

    return () => window.removeEventListener('keydown', onCarouselNavigate);
  }, [onCarouselNavigate]);

  // Removes event listener on main page if gallery is open so keyboard doesn't scroll both galleries
  useEffect(() => {
    if (!isLightbox && isGalleryOpen) {
      window.removeEventListener('keydown', onCarouselNavigate);
    } else {
      window.addEventListener('keydown', onCarouselNavigate);
    }
  }, [isGalleryOpen, isLightbox, onCarouselNavigate]);

  // Track touch/swipe metrics for mobile
  const [touch, setTouch] = useState({
    x: null,
    timeOfLastSwipe: null,
    totalOffset: 0,
    speed: 0,
    isSwipe: false
  });

  // Initialize on first touch
  const onTouchStart = e => {
    setTouch({
      x: e.targetTouches[0].clientX,
      timeOfLastSwipe: Date.now(),
      totalOffset: 0,
      speed: 0,
      isSwipe: false
    });
  };

  // Calculate offset and speed on movement
  const onTouchMove = e => {
    const currentTime = Date.now();
    const newX = e?.targetTouches[0].clientX;
    const newOffset = newX - touch.x;
    const timeElapsed = currentTime - touch.timeOfLastSwipe;
    const speed = newOffset / timeElapsed;

    setTouch(previous => ({
      ...previous,
      x: newX,
      speed,
      totalOffset: touch.totalOffset + newOffset,
      timeOfLastSwipe: currentTime,
      isSwipe: true
    }));
  };

  // Only navigate the carousel after the touch ends
  const onTouchEnd = () => {
    if (touch.speed < -1) {
      onCarouselPrev();
    } else if (touch.speed > 1) {
      onCarouselNext();
    }
  };

  const handleOpenGallery = () => {
    if (isLightbox) return;

    onGalleryOpen(selectedPhotoIndex);

    mp.track(mixpanelConfig.names.openRetailVdpGallery, {
      retail_guid: retailTrade?.guid,
      year: tradeDetails.year,
      make: tradeDetails.make,
      model: tradeDetails.model,
      vin: tradeDetails.vin,
      trim: tradeDetails.trim,
      platform: isMobileDevice() ? RetailPlatforms.Mobile : RetailPlatforms.Web,
      is_retail_customer:
        JSON.parse(window.localStorage.getItem('auth'))?.user === undefined,
      dealer: dealerName
    });
  };

  return media ? (
    <CarouselContainer isLightbox={isLightbox}>
      <div>
        {media?.length > 0 && (
          <CarouselMain isLightbox={isLightbox}>
            {media[selectedPhotoIndex] &&
            media[selectedPhotoIndex]?.mediaType !== RetailMediaType.VIDEO ? (
              <>
                {media[selectedPhotoIndex]?.mediaType ===
                  RetailMediaType.DAMAGE && (
                  <LabelContainer>
                    <StyledCarouselLabel>
                      {media[selectedPhotoIndex]?.label}
                    </StyledCarouselLabel>
                  </LabelContainer>
                )}
                <CarouselMainPhoto
                  data-testid="retailVdp-carousel-mainPhoto"
                  src={media[selectedPhotoIndex]?.url}
                  alt="Image Loading"
                  onClick={handleOpenGallery}
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                />
              </>
            ) : (
              <CarouselMainVideo
                data-testid="retailVdp-carousel-mainVideo"
                src={`${media[selectedPhotoIndex]?.url}#t=0.001`}
                alt="Video Loading"
                controls
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              />
            )}
            {media?.length > 1 && (
              <>
                <StyledCarouselPrev
                  data-testid="retailVdp-carousel-prev"
                  onClick={onCarouselPrev}
                >
                  <Icon icon="TR5-Prev_chev" />
                </StyledCarouselPrev>
                <StyledCarouselNext
                  data-testid="retailVdp-carousel-next"
                  onClick={onCarouselNext}
                >
                  <Icon icon="TR5-Next_chev" />
                </StyledCarouselNext>
                <CarouselCounter data-testid="retailVdp-carousel-counter">{`${selectedPhotoIndex +
                  1}/${media?.length}`}</CarouselCounter>
              </>
            )}
          </CarouselMain>
        )}
        {media?.length > 1 && (
          <ThumbnailsContainer isLightbox={isLightbox}>
            <CarouselThumbnails
              multiple={multiple}
              numberInCarousel={numberInCarousel}
            >
              {media.map((mediaItem, index) => (
                <CarouselItem
                  role="button"
                  data-testid={`retailVdp-carousel-thumbnail-${mediaItem.id}`}
                  key={`photo-${mediaItem.id}`}
                  onClick={() => onCarouselClick(index)}
                  numberInCarousel={numberInCarousel}
                >
                  {selectedPhotoIndex !== index && <PhotoOverlay />}
                  {mediaItem.mediaType !== RetailMediaType.VIDEO ? (
                    <CarouselPhoto src={mediaItem.url} alt="Loading..." />
                  ) : (
                    <CarouselVideo src={mediaItem.url} />
                  )}
                </CarouselItem>
              ))}
            </CarouselThumbnails>
          </ThumbnailsContainer>
        )}
      </div>
    </CarouselContainer>
  ) : (
    <CarouselContainer>
      <CarouselMain>
        <Loading width="500px" height="300px" />
      </CarouselMain>
    </CarouselContainer>
  );
};

export default Carousel;
